import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
import {
  CheckCircle,
  Email,
  LocalPhone,
  LocationOn,
  Send,
  SupportAgent,
  WaterDrop,
} from '@mui/icons-material';
import Navbar from './Navbar';
import Footer from './Footer';
import IP_URL from '../Components/connect';
import { Helmet } from 'react-helmet-async';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Main = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #f6f9fc;
  color: #0f172a;
`;

const Container = styled.main`
  flex: 1;
  padding-top: 76px;
`;

const Hero = styled.section`
  position: relative;
  isolation: isolate;
  overflow: hidden;
  background:
    radial-gradient(circle at 82% 18%, rgba(14, 165, 233, 0.34), transparent 34%),
    linear-gradient(135deg, #06111f 0%, #0b1f3a 45%, #075985 100%);
  color: white;
`;

const HeroInner = styled.div`
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: 76px 0 60px;
  text-align: center;
`;

const Eyebrow = styled.div`
  width: fit-content;
  margin: 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 13px;
  border: 1px solid rgba(186, 230, 253, 0.38);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  color: #bae6fd;
  font-size: 0.82rem;
  font-weight: 700;
  text-transform: uppercase;
`;

const Heading = styled.h1`
  max-width: 720px;
  margin: 20px auto 18px;
  font-size: clamp(2.2rem, 5vw, 3.6rem);
  line-height: 1.08;
  font-weight: 800;
  letter-spacing: 0;
`;

const HeroText = styled.p`
  max-width: 620px;
  margin: 0 auto;
  color: #dbeafe;
  font-size: clamp(1rem, 1.7vw, 1.15rem);
  line-height: 1.8;
`;

const Section = styled.section`
  width: min(1180px, calc(100% - 36px));
  margin: 0 auto;
  padding: 70px 0 90px;
`;

const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.1fr) minmax(300px, 0.9fr);
  gap: clamp(28px, 5vw, 48px);
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FormCard = styled.div`
  padding: clamp(24px, 4vw, 38px);
  border: 1px solid #e2e8f0;
  border-radius: 22px;
  background: white;
  box-shadow: 0 22px 60px rgba(15, 23, 42, 0.08);
`;

const CardTitle = styled.h2`
  margin: 0 0 8px;
  color: #0f172a;
  font-size: 1.4rem;
  font-weight: 800;
`;

const CardSubtitle = styled.p`
  margin: 0 0 26px;
  color: #64748b;
  font-size: 0.96rem;
  line-height: 1.6;
`;

const Form = styled.form`
  display: grid;
  gap: 16px;
`;

const Field = styled.div`
  display: grid;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 0.86rem;
  font-weight: 700;
  color: #334155;
`;

const Input = styled.input`
  height: 48px;
  padding: 0 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.96rem;
  background: #f8fafc;
  color: #0f172a;
  outline: none;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:focus {
    border-color: #2563eb;
    background: white;
  }
`;

const TextArea = styled.textarea`
  min-height: 130px;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  font-size: 0.96rem;
  font-family: inherit;
  background: #f8fafc;
  color: #0f172a;
  outline: none;
  resize: vertical;
  transition: border-color 0.2s ease, background 0.2s ease;

  &:focus {
    border-color: #2563eb;
    background: white;
  }
`;

const Button = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 50px;
  padding: 0 22px;
  margin-top: 4px;
  border: none;
  border-radius: 12px;
  background: #2563eb;
  color: white;
  font-size: 0.98rem;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  box-shadow: 0 18px 35px rgba(37, 99, 235, 0.28);

  &:hover {
    background: #1d4ed8;
    transform: translateY(-2px);
    box-shadow: 0 22px 45px rgba(37, 99, 235, 0.34);
  }

  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const InfoStack = styled.div`
  display: grid;
  gap: 18px;
`;

const InfoCard = styled.div`
  padding: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 18px;
  background: white;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.055);
`;

const InfoCardTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 14px;
  color: #0f172a;
  font-size: 1.02rem;
  font-weight: 800;
`;

const IconBox = styled.div`
  width: 36px;
  height: 36px;
  display: grid;
  place-items: center;
  border-radius: 10px;
  background: #eff6ff;
  color: #2563eb;
`;

const ContactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  color: #475569;
  font-size: 0.94rem;
  line-height: 1.6;

  & + & {
    margin-top: 10px;
  }

  svg {
    flex-shrink: 0;
    font-size: 1.05rem;
    color: #0ea5e9;
  }
`;

const DarkInfoCard = styled(InfoCard)`
  background:
    radial-gradient(circle at 90% 0%, rgba(125, 211, 252, 0.22), transparent 40%),
    linear-gradient(135deg, #071428, #0f3a5f);
  border-color: transparent;
  color: white;

  h3 {
    color: white;
  }

  p {
    margin: 0;
    color: #dbeafe;
    font-size: 0.92rem;
    line-height: 1.7;
  }
`;

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 999;
`;

const SuccessModal = styled(motion.div)`
  position: fixed;
  top: 50%;
  left: 50%;
  background: white;
  color: #0f172a;
  padding: 34px;
  border-radius: 20px;
  box-shadow: 0 25px 70px rgba(0, 0, 0, 0.3);
  width: 90%;
  max-width: 400px;
  text-align: center;
  z-index: 1000;

  svg {
    color: #16a34a;
    font-size: 2.6rem;
    margin-bottom: 10px;
  }

  h2 {
    margin: 0 0 10px;
    font-size: 1.25rem;
  }

  p {
    margin: 0 0 20px;
    color: #64748b;
    line-height: 1.6;
  }
`;

const ContactUs = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const validateForm = () => {
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      toast.error('Please fill out all fields.');
      return false;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address.');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSending(true);
    try {
      const response = await fetch(`${IP_URL}/mail/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setFormData({ name: '', email: '', message: '' });
        setShowModal(true);
        toast.success('Your message was sent successfully!');
      } else {
        toast.error('Error sending message. Please try again.');
      }
    } catch {
      toast.error('Network error. Please check your connection.');
    } finally {
      setSending(false);
    }
  };

  return (
    <Main>
      <Helmet>
        <title>Contact AquaTools | Smart Water Solutions</title>
        <meta
          name="description"
          content="Get in touch with AquaTools for smart metering solutions, support inquiries, or partnership opportunities. We respond promptly to every message."
        />
        <meta
          name="keywords"
          content="contact aquatools, water metering support, IoT solutions, smart metering kenya, customer support, partnership inquiry"
        />
        <meta name="robots" content="index, follow" />
      </Helmet>

      <Navbar />

      <Container>
        <Hero>
          <HeroInner>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55 }}
            >
              <Eyebrow>
                <WaterDrop fontSize="small" />
                Get in touch
              </Eyebrow>
              <Heading>Contact AquaTools</Heading>
              <HeroText>
                We're here to help you succeed. Whether you're exploring our smart water solutions,
                need technical support, or want to collaborate — our team will respond promptly and
                professionally.
              </HeroText>
            </motion.div>
          </HeroInner>
        </Hero>

        <Section>
          <SplitGrid>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5 }}
            >
              <FormCard>
                <CardTitle>Send us a message</CardTitle>
                <CardSubtitle>
                  Fill out the form below and a member of our team will get back to you shortly.
                </CardSubtitle>
                <Form onSubmit={handleSubmit}>
                  <Field>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      type="text"
                      name="name"
                      placeholder="Jane Doe"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="email">Email Address</Label>
                    <Input
                      id="email"
                      type="email"
                      name="email"
                      placeholder="jane@company.com"
                      value={formData.email}
                      onChange={handleChange}
                    />
                  </Field>
                  <Field>
                    <Label htmlFor="message">Message</Label>
                    <TextArea
                      id="message"
                      name="message"
                      placeholder="Tell us about your water metering needs..."
                      value={formData.message}
                      onChange={handleChange}
                    />
                  </Field>
                  <Button type="submit" disabled={sending}>
                    {sending ? 'Sending...' : (
                      <>
                        Send Message
                        <Send fontSize="small" />
                      </>
                    )}
                  </Button>
                </Form>
              </FormCard>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <InfoStack>
                <InfoCard>
                  <InfoCardTitle>
                    <IconBox>
                      <SupportAgent fontSize="small" />
                    </IconBox>
                    Help Desk
                  </InfoCardTitle>
                  <ContactRow>
                    <Email />
                    info@aquatools.io
                  </ContactRow>
                  <ContactRow>
                    <LocalPhone />
                    +254 729 497 037
                  </ContactRow>
                </InfoCard>

                <InfoCard>
                  <InfoCardTitle>
                    <IconBox>
                      <SupportAgent fontSize="small" />
                    </IconBox>
                    IT Support
                  </InfoCardTitle>
                  <ContactRow>
                    <Email />
                    support@aquatools.io
                  </ContactRow>
                  <ContactRow>
                    <LocalPhone />
                    +254 713 522 833
                  </ContactRow>
                </InfoCard>

                <DarkInfoCard>
                  <InfoCardTitle>
                    <IconBox style={{ background: 'rgba(255,255,255,0.12)', color: '#38bdf8' }}>
                      <LocationOn fontSize="small" />
                    </IconBox>
                    Our Office
                  </InfoCardTitle>
                  <p>Nairobi, Kenya — supporting utilities and installations nationwide.</p>
                </DarkInfoCard>
              </InfoStack>
            </motion.div>
          </SplitGrid>
        </Section>
      </Container>

      <Footer />

      <AnimatePresence>
        {showModal && (
          <>
            <Overlay onClick={() => setShowModal(false)} />
            <SuccessModal
              initial={{ opacity: 0, scale: 0.85, x: '-50%', y: '-50%' }}
              animate={{ opacity: 1, scale: 1, x: '-50%', y: '-50%' }}
              exit={{ opacity: 0, scale: 0.85, x: '-50%', y: '-50%' }}
              transition={{ duration: 0.25 }}
            >
              <CheckCircle />
              <h2>Message Sent!</h2>
              <p>Thank you for contacting AquaTools. We'll get back to you shortly.</p>
              <Button onClick={() => setShowModal(false)} style={{ width: '100%' }}>
                Close
              </Button>
            </SuccessModal>
          </>
        )}
      </AnimatePresence>
    </Main>
  );
};

export default ContactUs;
